import { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Lock, Clock, XCircle, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

const Revealed = ({ l }) => {
  const wa = l.contact_phone?.replace(/\D/g, "").replace(/^0/, "966");
  return (
    <div className="space-y-3" data-testid="contact-revealed">
      <div className="text-sm text-slate-500">صاحب الإعلان: <span className="font-bold text-slate-800">{l.owner_display}</span></div>
      <div className="text-2xl font-extrabold tracking-wider text-brand" dir="ltr" data-testid="contact-phone">{l.contact_phone}</div>
      <div className="flex gap-2">
        <a href={`tel:${l.contact_phone}`} data-testid="contact-call-btn" className="flex-1 flex items-center justify-center gap-1.5 bg-brand text-white rounded-lg py-2.5 text-sm font-bold"><Phone size={16} />اتصال</a>
        <a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="flex-1 flex items-center justify-center gap-1.5 bg-emerald-600 text-white rounded-lg py-2.5 text-sm font-bold"><MessageCircle size={16} />واتساب</a>
      </div>
    </div>
  );
};

export const ContactPanel = ({ l, onChange }) => {
  const { user, login } = useAuth();
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const isJob = l.type === "job";

  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/listings/${l.id}/contact-request`, { message: msg });
      toast.success("تم إرسال طلبك للإدارة، سيظهر الرقم بعد الموافقة");
      onChange();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  let body;
  if (l.contact_phone) body = <Revealed l={l} />;
  else if (!user) body = <Button data-testid="contact-login-btn" onClick={login} className="w-full rounded-lg bg-brand hover:bg-brand-dark">سجّل الدخول لطلب التواصل</Button>;
  else if (l.contact_status === "pending") body = <div data-testid="contact-pending" className="flex items-center gap-2 text-amber-700 bg-amber-50 rounded-xl p-3 text-sm"><Clock size={16} />طلبك قيد مراجعة الإدارة</div>;
  else if (l.contact_status === "rejected") body = <div data-testid="contact-rejected" className="flex items-center gap-2 text-red-700 bg-red-50 rounded-xl p-3 text-sm"><XCircle size={16} />تم رفض طلب التواصل</div>;
  else if (!user.is_admin && user.role !== "buyer") body = <div data-testid="contact-not-buyer-note" className="text-sm bg-amber-50 text-amber-800 rounded-xl p-3">طلب التواصل متاح لحسابات المشترين فقط. <Link to="/dashboard" className="font-bold underline">غيّر نوع حسابك</Link></div>;
  else body = (
    <div className="space-y-3">
      <div className="text-2xl font-bold tracking-widest text-slate-300" dir="ltr">05•• ••• •••</div>
      <Textarea data-testid="contact-message-input" value={msg} onChange={(e) => setMsg(e.target.value)} placeholder={isJob ? "نبذة مختصرة عن خبرتك (اختياري)" : "رسالة للإدارة (اختياري)"} className="rounded-xl" />
      <Button data-testid="contact-request-btn" disabled={busy} onClick={send} className="w-full rounded-lg bg-sun hover:bg-sun-dark">{isJob ? "التقديم وطلب رقم التواصل" : "طلب عرض رقم التواصل"}</Button>
    </div>
  );

  return (
    <div className="bg-white border rounded-xl p-5" data-testid="contact-panel">
      <div className="flex items-center gap-2 font-bold mb-1"><Lock size={16} className="text-brand" />معلومات التواصل</div>
      <p className="text-xs text-slate-500 mb-4">لا تظهر الأرقام إلا بعد موافقة إدارة الموقع.</p>
      {body}
    </div>
  );
};
